import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  FileText,
  Users,
  CheckSquare,
  CreditCard,
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  ChevronRight,
  User,
  Building2,
  Shield,
  FileImage,
} from 'lucide-react';
import { NotificationBell } from '../shared/NotificationBell';

import { authApi, departmentsApi } from '../../lib/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_bab98220-a396-492d-90c6-b545ef026af6/artifacts/jwqj5vlw_logo_transparent.de5a7eae8bd4251a0668.png';

const navItems = {
  employee: [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/vouchers', label: 'My Vouchers', icon: FileText },
    { href: '/vouchers/new', label: 'New Voucher', icon: FileText },
    { href: '/department-vouchers', label: 'Department Vouchers', icon: Building2 },
    { href: '/pdf-tools', label: 'PDF Tools', icon: FileImage },
  ],
  manager: [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/vouchers', label: 'My Vouchers', icon: FileText },
    { href: '/vouchers/new', label: 'New Voucher', icon: FileText },
    { href: '/department-vouchers', label: 'Department Vouchers', icon: Building2 },
    { href: '/approvals', label: 'Pending Approvals', icon: CheckSquare },
    { href: '/manager/vouchers', label: 'All Vouchers', icon: FileText },
    { href: '/pdf-tools', label: 'PDF Tools', icon: FileImage },
  ],
  internal_audit: [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/internal-audit/queue', label: 'Audit Queue', icon: CheckSquare },
    { href: '/internal-audit/all', label: 'All Vouchers', icon: FileText },
    { href: '/pdf-tools', label: 'PDF Tools', icon: FileImage },
  ],
  finance_head: [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/vouchers', label: 'My Vouchers', icon: FileText },
    { href: '/vouchers/new', label: 'New Voucher', icon: FileText },
    { href: '/finance-head/queue', label: 'My Approvals', icon: CheckSquare },
    { href: '/finance-head/rejected', label: 'Rejected Vouchers', icon: FileText },
    { href: '/pdf-tools', label: 'PDF Tools', icon: FileImage },
  ],
  accounts: [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/accounts/vouchers', label: 'My Vouchers', icon: FileText },
    { href: '/accounts/vouchers/new', label: 'New Voucher', icon: FileText },
    { href: '/accounts/queue', label: 'Approval Queue', icon: CreditCard },
    { href: '/accounts/all', label: 'All Vouchers', icon: FileText },
    { href: '/pdf-tools', label: 'PDF Tools', icon: FileImage },
  ],
  admin: [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/admin/users', label: 'User Management', icon: Users },
    { href: '/admin/departments', label: 'Departments', icon: Building2 },
    { href: '/admin/vouchers', label: 'All Vouchers', icon: FileText },
    { href: '/admin/audit-logs', label: 'Audit Logs', icon: Shield },
    { href: '/pdf-tools', label: 'PDF Tools', icon: FileImage },
  ],
};

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasPdfToolsAccess, setHasPdfToolsAccess] = useState(false);

  // Check PDF Tools access on mount
  useEffect(() => {
    const checkPdfAccess = async () => {
      // Admin always has access
      if (user?.role === 'admin') {
        setHasPdfToolsAccess(true);
        return;
      }
      
      try {
        const accessData = await departmentsApi.getUserDepartmentAccess();
        setHasPdfToolsAccess(accessData.hasPdfToolsAccess || false);
      } catch (error) {
        console.error('Failed to check PDF tools access:', error);
        setHasPdfToolsAccess(false);
      }
    };
    
    if (user) {
      checkPdfAccess();
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    logout();
    navigate('/login');
  };

  const items = navItems[user?.role] || navItems.employee;
  
  // Filter out PDF Tools if user doesn't have access
  const filteredItems = items.filter(item => {
    if (item.href === '/pdf-tools') {
      return hasPdfToolsAccess;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white/80 backdrop-blur-xl border-b border-black/5 z-50">
        <div className="flex items-center justify-between h-full px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-md transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="Al Arabia" 
                className="h-14 w-auto"
              />
            </Link>
          </div>

          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            <NotificationBell />
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 h-10 px-3"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-8 h-8 rounded-full bg-[#6a5da4] flex items-center justify-center text-white text-sm font-medium">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{user?.name}</span>
                    <span className="text-xs text-muted-foreground">{user?.email}</span>
                    <span className="text-xs text-muted-foreground capitalize mt-1">
                      Role: {user?.role}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-16 left-0 bottom-0 w-64 bg-white border-r border-gray-200 z-40 transform transition-transform duration-200 ease-out",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <nav className="p-4 space-y-1">
          {filteredItems.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-[#6a5da4] text-white"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                )}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon size={18} />
                <span>{item.label}</span>
                {isActive && <ChevronRight size={16} className="ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* User Info Card */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#6a5da4]/10 flex items-center justify-center">
                <User size={18} className="text-[#6a5da4]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 min-h-screen">
        <div className="p-6 lg:p-8 animate-fade-in">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
